/**
 * Parse natural language task input
 * Supports: dates, times, tags, priority
 * 
 * Examples:
 * - "Call customer tomorrow 10:00" → dueAt = tomorrow at 10:00
 * - "Fix bug fri 9:30 #vehicle:ABC123 @p1" → dueAt = Friday 9:30, tags, priority 1
 * - "Review invoice next monday 14:00" → dueAt = next Monday at 14:00
 */

import { startOfDay, addDays, setHours, setMinutes, nextDay, parse } from 'date-fns';
import { TaskInput } from '@/types/notepad';

interface ParsedTask {
  title: string;
  dueAt?: Date;
  tags?: string[];
  priority?: 1 | 2 | 3 | 4 | 5;
}

/**
 * Parse task input with NLP
 */
export function parseTaskInput(input: string): ParsedTask {
  let title = input.trim();
  let dueAt: Date | undefined;
  const tags: string[] = [];
  let priority: 1 | 2 | 3 | 4 | 5 | undefined;

  // Extract priority (@p1, @p2, etc.)
  const priorityMatch = title.match(/@p([1-5])/i);
  if (priorityMatch) {
    priority = parseInt(priorityMatch[1]) as 1 | 2 | 3 | 4 | 5;
    title = title.replace(/@p[1-5]/gi, '').trim();
  }

  // Extract tags (#tag:value or #tag)
  const tagMatches = title.matchAll(/#([\w:]+)/g);
  for (const match of tagMatches) {
    tags.push(match[1]);
    title = title.replace(match[0], '').trim();
  }

  // Parse date and time
  const now = new Date();
  const today = startOfDay(now);

  // Tomorrow
  if (/\btomorrow\b/i.test(title)) {
    dueAt = addDays(today, 1);
    title = title.replace(/\btomorrow\b/gi, '').trim();
  }
  // Today
  else if (/\btoday\b/i.test(title)) {
    dueAt = today;
    title = title.replace(/\btoday\b/gi, '').trim();
  }
  // Day names (mon, tue, wed, thu, fri, sat, sun)
  else {
    const dayMatch = title.match(/\b(mon|tue|wed|thu|fri|sat|sun|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i);
    if (dayMatch) {
      const dayName = dayMatch[1].toLowerCase();
      const dayMap: Record<string, number> = {
        mon: 1, monday: 1,
        tue: 2, tuesday: 2,
        wed: 3, wednesday: 3,
        thu: 4, thursday: 4,
        fri: 5, friday: 5,
        sat: 6, saturday: 6,
        sun: 0, sunday: 0,
      };
      
      const targetDay = dayMap[dayName];
      if (targetDay !== undefined) {
        dueAt = nextDay(today, targetDay as 0 | 1 | 2 | 3 | 4 | 5 | 6);
        title = title.replace(/\b(mon|tue|wed|thu|fri|sat|sun|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/gi, '').trim();
      }
    }
  }

  // Parse time (HH:mm or H:mm)
  const timeMatch = title.match(/\b(\d{1,2}):(\d{2})\b/);
  if (timeMatch) {
    const hours = parseInt(timeMatch[1]);
    const minutes = parseInt(timeMatch[2]);
    
    if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
      if (!dueAt) {
        // If no date specified, assume today if time is in future, otherwise tomorrow
        const timeToday = setMinutes(setHours(today, hours), minutes);
        dueAt = timeToday > now ? timeToday : addDays(timeToday, 1);
      } else {
        dueAt = setMinutes(setHours(dueAt, hours), minutes);
      }
      title = title.replace(/\b\d{1,2}:\d{2}\b/, '').trim();
    }
  }

  // Clean up extra whitespace
  title = title.replace(/\s+/g, ' ').trim();

  return {
    title,
    dueAt,
    tags: tags.length > 0 ? tags : undefined,
    priority,
  };
}

/**
 * Format date for display
 */
export function formatDueDate(date: Date): string {
  const now = new Date();
  const today = startOfDay(now);
  const tomorrow = addDays(today, 1);
  const dateStart = startOfDay(date);

  if (dateStart.getTime() === today.getTime()) {
    return `Today ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  } else if (dateStart.getTime() === tomorrow.getTime()) {
    return `Tomorrow ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  } else if (dateStart.getTime() < today.getTime()) {
    return `Overdue - ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
  } else {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }
}
